class TestimonialCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-testimonials-track]');
    this.prevButton = this.querySelector('[data-direction="prev"]');
    this.nextButton = this.querySelector('[data-direction="next"]');

    if (!this.track) return;

    this.prevButton?.addEventListener('click', () => this.scrollByCard(-1));
    this.nextButton?.addEventListener('click', () => this.scrollByCard(1));
    this.track.addEventListener('scroll', () => this.updateButtonStates(), { passive: true });
    window.addEventListener('resize', () => this.updateButtonStates());

    this.setupDrag();
    this.setupAutoplay();
    this.updateButtonStates();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  scrollByCard(direction) {
    const card = this.track.querySelector('[data-testimonials-card]');
    if (!card) return;

    const trackStyle = getComputedStyle(this.track);
    const gap = parseFloat(trackStyle.columnGap || trackStyle.gap || '0');
    const distance = card.getBoundingClientRect().width + gap;
    const maxScrollLeft = this.track.scrollWidth - this.track.clientWidth - 1;

    // Wrap back to the start once autoplay/next runs past the last card, instead of
    // just stopping there — client asked for continuous auto-sliding, not a one-shot.
    if (direction > 0 && this.track.scrollLeft >= maxScrollLeft) {
      this.track.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }

    this.track.scrollBy({ left: distance * direction, behavior: 'smooth' });
  }

  updateButtonStates() {
    const maxScrollLeft = this.track.scrollWidth - this.track.clientWidth - 1;

    if (this.prevButton) this.prevButton.disabled = this.track.scrollLeft <= 0;
    if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maxScrollLeft;
  }

  // The track already scrolls natively via touch on mobile (overflow-x + touch-action).
  // Mouse users have no equivalent — a plain scroll container isn't click-and-drag —
  // so this adds that drag behavior for desktop/trackpad-with-mouse use.
  setupDrag() {
    let isDown = false;
    let dragged = false;
    let startX = 0;
    let startScrollLeft = 0;

    this.track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return; // native touch scrolling handles this
      isDown = true;
      dragged = false;
      startX = event.clientX;
      startScrollLeft = this.track.scrollLeft;
      this.track.classList.add('is-dragging');
      this.pauseAutoplay();
    });

    this.track.addEventListener('pointermove', (event) => {
      if (!isDown) return;
      const delta = event.clientX - startX;
      if (Math.abs(delta) > 3) dragged = true;
      this.track.scrollLeft = startScrollLeft - delta;
    });

    const endDrag = () => {
      if (!isDown) return;
      isDown = false;
      this.track.classList.remove('is-dragging');
      this.resumeAutoplay();
    };

    this.track.addEventListener('pointerup', endDrag);
    this.track.addEventListener('pointerleave', endDrag);

    // Dragging shouldn't also fire the card's own link — only suppress the click
    // when the pointer actually moved, so a plain tap/click still works.
    this.track.addEventListener(
      'click',
      (event) => {
        if (dragged) {
          event.preventDefault();
          event.stopPropagation();
          dragged = false;
        }
      },
      true
    );
  }

  setupAutoplay() {
    this.autoplayDelay = 4000;
    this.track.addEventListener('pointerenter', () => this.pauseAutoplay());
    this.track.addEventListener('pointerleave', () => this.resumeAutoplay());
    this.resumeAutoplay();
  }

  resumeAutoplay() {
    this.stopAutoplay();
    this.autoplayTimer = window.setInterval(() => this.scrollByCard(1), this.autoplayDelay);
  }

  pauseAutoplay() {
    this.stopAutoplay();
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
}

if (!customElements.get('testimonial-carousel')) {
  customElements.define('testimonial-carousel', TestimonialCarousel);
}
